import { getMessage } from '@tcc/i18n';
import { Nullable } from '@tcc/common';
import { Typography, TypographyVariant, Container, ComponentPadding, Orientation, DensitySpacing, ComponentFill, ComponentColor, ComponentStrength, ComponentAlign } from '@tcc/fds';
import { SummaryItem } from './SummaryItem';
import { ReportMeasurement } from '../../../../types';
import { getLocalizedArea, getLocalizedAverageYield, getLocalizedMoisture, getLocalizedTotalWeight } from '../frrStatsFormat';

export type RegionReportSummaryProps = {
  area: Nullable<ReportMeasurement>;
  averageMoisture?: Nullable<ReportMeasurement>;
  averageYield: Nullable<ReportMeasurement>;
  totalWeight: Nullable<ReportMeasurement>;
  averageYieldText: string;
  withDryMatterYield: boolean;
  cropCode: string;
  cropNominalWeight?: Nullable<number | ReportMeasurement>;
};

export const RegionReportSummary = ({
  averageYield,
  averageYieldText,
  totalWeight,
  area,
  averageMoisture,
  withDryMatterYield,
  cropCode,
  cropNominalWeight
}: RegionReportSummaryProps) => {
  const totalWeightLabelKeySuffix = withDryMatterYield ? 'totalDryWeight' : 'totalWeight';
  const totalWeightLabelKey = `regions.report.modal.${totalWeightLabelKeySuffix}`;

  const localizedArea = area ? getLocalizedArea(area) : undefined;
  const localizedMoisture = averageMoisture ? getLocalizedMoisture(cropCode, averageMoisture) : undefined;
  const localizedAverageYield = averageYield ?
    getLocalizedAverageYield(cropCode, averageYield, cropNominalWeight) : undefined;
  const localizedTotalWeight = totalWeight ? getLocalizedTotalWeight({
    cropCode,
    totalWeight,
    cropNominalWeight
  }) : undefined;

  return (
    <Container
      testId="frr-summary-title"
      fill={ComponentFill.Clean}
      color={ComponentColor.Primary}
      strength={ComponentStrength.Soft}
      padding={ComponentPadding.Container}
    >
      <Container
      transparent
      orientation={Orientation.Vertical}
      color={ComponentColor.Neutral}
      align={ComponentAlign.Start}
      >
        <Typography tag='h2' variant={TypographyVariant.Heading} strength={ComponentStrength.Strong}>
          {getMessage('regions.report.modal.summaryTitle')}
        </Typography>
        <Container
          orientation={Orientation.Horizontal}
          gap={DensitySpacing.Related}
          testId="frr-summary-totals"
          transparent
          >
          <SummaryItem
            localizedMeasurement={localizedArea}
            label={getMessage('regions.report.modal.areaHarvested')}
            dataTestId="harvested" />

          { localizedMoisture && (
            <SummaryItem
              localizedMeasurement={localizedMoisture}
              label={getMessage('regions.report.modal.averageMoisture')}
              dataTestId="moisture" />
          )}

          <SummaryItem
            localizedMeasurement={localizedAverageYield}
            label={averageYieldText}
            dataTestId='avg-yield-text' />

          { localizedTotalWeight && (
            <SummaryItem
              localizedMeasurement={localizedTotalWeight}
              label={getMessage(totalWeightLabelKey)}
              dataTestId='total-weight' />
          )}
        </Container>
      </Container>
  </Container>
  )
};
